import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Divider,
  Tooltip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import type { Customer } from '../models/Customer';
import type { Order } from '../models/Order';
import orderService from '../services/orderService';
import OrderDetailsDialog from './OrderDetailsDialog';

interface Props {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSaved?: () => void; // optionnel: permet au parent de rafraîchir la liste si besoin
}

type OrdersPage = {
  orders: Order[];
  nextCursor?: any;
};

const CustomerProfileDialog: React.FC<Props> = ({ open, customer, onClose }) => {
  const [orders, setOrders] = useState<OrdersPage>({ orders: [] });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    // reset orders when opening
    setOrders({ orders: [] });
  }, [open]);

  const copyToClipboard = (value: string) => {
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => {});
  };

  const loadOrders = async (reset = false) => {
    if (!customer) return;
    try {
      setOrdersLoading(true);
      const res = await orderService.listOrdersByUser(customer.id, 20, reset ? undefined : orders.nextCursor);
      const merged = reset ? res.orders : [...orders.orders, ...res.orders];
      setOrders({ orders: merged, nextCursor: res.nextCursor });
    } catch {
      // ignore
    } finally {
      setOrdersLoading(false);
    }
  };

  // Auto load first page when opening and we have a user
  useEffect(() => {
    if (open && customer) {
      loadOrders(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id]);

  const openAddressOnMap = (a: NonNullable<Customer['addresses']>[number]) => {
    // Priorité aux coordonnées si valides
    if (Number.isFinite(a.latitude) && Number.isFinite(a.longitude)) {
      const url = `https://www.google.com/maps?q=${a.latitude},${a.longitude}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Sinon, construire une requête d'adresse
    const parts = [a.street, a.city, a.region, a.postalCode].filter(Boolean).join(', ');
    if (parts) {
      const url = `https://www.google.com/maps?q=${encodeURIComponent(parts)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const openOrderDetails = (o: Order) => {
    setSelectedOrder(o);
    setOrderDialogOpen(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Détails du client</DialogTitle>
      <DialogContent dividers>
        {!customer ? (
          <Alert severity="info">Aucun client sélectionné</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Nom et statut */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">
                {`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Client'}
              </Typography>
              {customer.isBanned ? (
                <Chip
                  label="Banni"
                  size="small"
                  sx={{ bgcolor: '#d32f2f', color: '#fff' }}
                />
              ) : (
                <Chip
                  label="Non banni"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Téléphone / Email */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 80 }}>Téléphone</Typography>
                <Typography variant="body2">{customer.phone || 'N/A'}</Typography>
                {customer.phone ? (
                  <Tooltip title="Copier le téléphone">
                    <IconButton size="small" onClick={() => copyToClipboard(customer.phone)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 80 }}>Email</Typography>
                <Typography variant="body2">{customer.email || 'N/A'}</Typography>
                {customer.email ? (
                  <Tooltip title="Copier l'email">
                    <IconButton size="small" onClick={() => copyToClipboard(customer.email || '')}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>

            <Divider />

            {/* Adresses */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Adresses</Typography>
              {(!customer.addresses || customer.addresses.length === 0) ? (
                <Alert severity="info">Aucune adresse enregistrée</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {customer.addresses.map((a) => (
                    <Paper
                      key={a.id}
                      onClick={() => openAddressOnMap(a)}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        cursor: 'pointer'
                      }}
                      title="Voir sur Google Maps"
                    >
                      {a.isDefault ? <Chip label="Par défaut" size="small" color="success" /> : null}
                      <Typography variant="body2" sx={{ color: '#1976d2', textDecoration: 'underline' }}>
                        {[
                          a.street,
                          a.city,
                          a.region,
                          a.postalCode
                        ].filter(Boolean).join(', ')}
                      </Typography>
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {Number.isFinite(a.latitude) && Number.isFinite(a.longitude) ? (
                          <>
                            <LocationIcon fontSize="small" color="error" />
                            <Typography variant="caption" color="text.secondary">
                              ({a.latitude.toFixed(6)}, {a.longitude.toFixed(6)})
                            </Typography>
                          </>
                        ) : null}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Commandes */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Commandes</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Magasin</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.orders.map((o) => (
                      <TableRow
                        key={o.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openOrderDetails(o)}
                      >
                        <TableCell>{o.id}</TableCell>
                        <TableCell>{o.createdAt.toLocaleString('fr-FR')}</TableCell>
                        <TableCell>{o.storeName}</TableCell>
                        <TableCell align="right">{o.totalAmount.toFixed(2)} MAD</TableCell>
                        <TableCell>{o.status}</TableCell>
                      </TableRow>
                    ))}
                    {orders.orders.length === 0 && !ordersLoading && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Alert severity="info">Aucune commande</Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {(orders.nextCursor || ordersLoading) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Button variant="outlined" onClick={() => loadOrders(false)} disabled={ordersLoading}>
                    {ordersLoading ? 'Chargement...' : 'Charger plus'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Dialog détails de commande */}
      <OrderDetailsDialog
        open={orderDialogOpen}
        onClose={() => { setOrderDialogOpen(false); setSelectedOrder(null); }}
        order={selectedOrder}
      />

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerProfileDialog;
